<template>
	<form class="personalization_step" novalidate @submit.prevent="submit">
		<h1 class="step_title">Personalización</h1>
		<p class="step_subtitle">Agrega tu logo y detalles básicos.</p>

		<div class="summary_card">
			<div class="summary_card-band"></div>
			<button
				type="button"
				class="logo_picker"
				:aria-label="logoPreview ? 'Cambiar logo' : 'Subir logo'"
				@click="openLogoPicker"
			>
				<img v-if="logoPreview" class="logo_preview" :src="logoPreview" alt="" />
				<b-icon v-else pack="fas" icon="image" size="is-small" />
			</button>
			<input
				ref="logoInput"
				class="logo_input"
				type="file"
				accept="image/png,image/jpeg"
				@change="onLogoChange"
			/>
			<p class="summary_name">{{ companyName }}</p>
			<p class="summary_meta">{{ companyRut }}</p>
			<p class="summary_meta">{{ email }}</p>
		</div>

		<p v-if="logoError" class="field_error logo_error">{{ logoError }}</p>

		<div class="field">
			<label class="field_label" for="register_fantasy_name">
				Nombre de fantasía (opcional)
			</label>
			<input
				id="register_fantasy_name"
				v-model.trim="fantasyName"
				class="field_input"
				type="text"
				maxlength="149"
				placeholder="Ingresa un nombre de fantasía"
			/>
		</div>

		<div class="field">
			<label class="field_label" for="register_company_name_display">
				Nombre de la empresa
			</label>
			<input
				id="register_company_name_display"
				class="field_input"
				type="text"
				:value="companyName"
				disabled
			/>
		</div>

		<p v-if="saveError" class="field_error">
			No pudimos guardar tu personalización. Inténtalo nuevamente.
		</p>

		<button class="continue_button" type="submit" :disabled="isSaving">
			Continuar
		</button>
	</form>
</template>

<script>
import { personalizeCompany } from "@/api/company";
import { downscaleImageFile } from "@/utils/image";
import { consoleHandlerError } from "@/utils/message";

// El logo se muestra chico (avatar 32px / preview 72px): lo acotamos en origen
// a 256px y validamos tipo/tamaño antes de subir. El backend igual revalida.
const LOGO_MAX_DIMENSION = 256;
const LOGO_MAX_SOURCE_BYTES = 8 * 1024 * 1024;
const LOGO_ALLOWED_TYPES = ["image/png", "image/jpeg"];

export default {
	name: "RegisterPersonalizationStep",
	props: {
		value: {
			type: Object,
			default: () => ({}),
		},
	},
	data() {
		return {
			fantasyName: this.value.fantasy_name || "",
			logoFile: null,
			logoPreview: null,
			logoError: "",
			isSaving: false,
			saveError: false,
		};
	},
	computed: {
		companyName() {
			return this.value.company_name || "";
		},
		companyRut() {
			return this.value.company_rut || "";
		},
		email() {
			return this.value.email || "";
		},
	},
	beforeDestroy() {
		if (this.logoPreview) {
			URL.revokeObjectURL(this.logoPreview);
		}
	},
	methods: {
		openLogoPicker() {
			this.$refs.logoInput.click();
		},
		async onLogoChange(event) {
			const file = event.target.files && event.target.files[0];
			// Reset del input: permite re-elegir el mismo archivo y re-disparar.
			event.target.value = "";
			if (!file) return;

			this.logoError = "";
			if (!LOGO_ALLOWED_TYPES.includes(file.type)) {
				this.logoError = "Formato no soportado. Usa PNG o JPG.";
				return;
			}
			if (file.size > LOGO_MAX_SOURCE_BYTES) {
				this.logoError = "La imagen es demasiado pesada (máx 8MB).";
				return;
			}

			// Achicamos en origen a 256px; si el procesamiento falla, subimos el
			// original (el backend igual topa el tamaño y valida el formato).
			try {
				const resized = await downscaleImageFile(file, LOGO_MAX_DIMENSION);
				this.setLogo(resized);
			} catch (error) {
				consoleHandlerError({ error, silent: true, prefix: "[register]" });
				this.setLogo(file);
			}
		},
		setLogo(file) {
			this.logoFile = file;
			if (this.logoPreview) {
				URL.revokeObjectURL(this.logoPreview);
			}
			this.logoPreview = URL.createObjectURL(file);
		},
		async submit() {
			this.isSaving = true;
			this.saveError = false;
			try {
				// Ambos campos son opcionales: solo persistimos si hay algo.
				if (this.logoFile || this.fantasyName) {
					await personalizeCompany({
						fantasyName: this.fantasyName,
						logoFile: this.logoFile,
					});
				}
				this.$emit("continue", { fantasy_name: this.fantasyName });
			} catch (error) {
				// La cuenta ya existe; el guardado de personalización puede
				// reintentarse sin perder el alta. Mostramos el error y dejamos
				// el botón activo en vez de avanzar perdiendo el logo/nombre.
				consoleHandlerError({ error, silent: true, prefix: "[register]" });
				this.saveError = true;
			} finally {
				this.isSaving = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.summary_card {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-bottom: 28px;
	padding: 0 24px 24px;
	background: var(--auth-surface);
	border: 1px solid var(--auth-border-soft);
	border-radius: 16px;
	overflow: hidden;
}

// Banda superior teal tenue detrás del logo.
.summary_card-band {
	align-self: stretch;
	height: 56px;
	margin: 0 -24px 0;
	background: var(--auth-panel-bg);
	opacity: 0.12;
}

.logo_picker {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 72px;
	height: 72px;
	margin-top: -36px;
	margin-bottom: 14px;
	padding: 0;
	background: var(--auth-surface);
	border: 1px solid var(--auth-border-soft);
	border-radius: 50%;
	color: var(--auth-text-muted);
	cursor: pointer;
	overflow: hidden;
	box-shadow: 0 1px 3px rgba(22, 36, 47, 0.12);
	transition: border-color 0.15s ease-out;

	&:hover {
		border-color: var(--auth-accent);
		color: var(--auth-accent);
	}
}

.logo_preview {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.logo_input {
	display: none;
}

.summary_name {
	color: var(--auth-text);
	font-size: 16px;
	font-weight: 700;
}

.summary_meta {
	color: var(--auth-text-secondary);
	font-size: 13px;
}
</style>
